import React, { useState, useCallback, useMemo } from 'react';
import { Icons } from '../utils/icons';
import { authService } from '../services/authService';
import { diasEspera as calcDias, claseUrgencia as diasColor, UMBRAL_ATENCION } from '../utils/urgencia';

// ─── Constantes ───────────────────────────────────────────────────────────────

const PRESUPUESTO_TYPE_ID = 898;
const UMBRAL_SEGUIMIENTO  = UMBRAL_ATENCION;  // días en estado Enviado antes de alertar (fuente única: utils/urgencia)

const ESTIMATE_STATES = {
  enviado:   651460, // Pendiente = enviado al cliente, esperando respuesta
  aceptado:  651465,
  rechazado: 651433,
  cerrado:   651432, // excluido del kanban
};

const KNOWN_STATES = new Set(Object.values(ESTIMATE_STATES));

const COLUMNAS_CONFIG = [
  {
    key: 'pendientesPresupuesto',
    label: 'Por presupuestar',
    color: 'border-blue-400',
    headerBg: 'bg-blue-50',
    headerText: 'text-blue-700',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
    dotColor: 'bg-blue-400',
    emptyMsg: 'Sin leads pendientes',
    showValue: false,
  },
  {
    key: 'presupuestoEnviado',
    label: 'Enviado al cliente',
    color: 'border-violet-400',
    headerBg: 'bg-violet-50',
    headerText: 'text-violet-700',
    badgeBg: 'bg-violet-100',
    badgeText: 'text-violet-700',
    dotColor: 'bg-violet-400',
    emptyMsg: 'Sin presupuestos enviados',
    showValue: true,
  },
  {
    key: 'presupuestoAceptado',
    label: 'Aceptado',
    color: 'border-emerald-400',
    headerBg: 'bg-emerald-50',
    headerText: 'text-emerald-700',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
    dotColor: 'bg-emerald-400',
    emptyMsg: 'Sin presupuestos aceptados',
    showValue: true,
  },
];

const FILTROS = ['todo', 'dia', 'semana', 'mes', 'año'];
const FILTROS_LABEL = { todo: 'Este año', dia: 'Día', semana: 'Semana', mes: 'Mes', año: 'Año' };

// ─── Utils ────────────────────────────────────────────────────────────────────

const toLocalDateStr = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// calcDias / diasColor ahora vienen de ../utils/urgencia (fuente única).

const formatEUR = (amount) =>
  (amount || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const columnValue = (cards) =>
  cards.filter((c) => c.cardType === 'presupuesto').reduce((s, c) => s + (c.totalAmount || 0), 0);

const AÑO_ACTUAL = String(new Date().getFullYear());

const getRango = (tipo, valor) => {
  if (tipo === 'todo') return { inicio: `${AÑO_ACTUAL}-01-01`, fin: `${AÑO_ACTUAL}-12-31` };
  if (!valor) return null;
  if (tipo === 'dia') return { inicio: valor, fin: valor };
  if (tipo === 'semana') {
    const [yearStr, weekStr] = valor.split('-W');
    const year = parseInt(yearStr, 10);
    const week = parseInt(weekStr, 10);
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - (dayOfWeek - 1) + (week - 1) * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { inicio: toLocalDateStr(monday), fin: toLocalDateStr(sunday) };
  }
  if (tipo === 'mes') {
    const [y, m] = valor.split('-').map(Number);
    const inicio = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const fin = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { inicio, fin };
  }
  if (tipo === 'año') return { inicio: `${valor}-01-01`, fin: `${valor}-12-31` };
  return null;
};

const aplicarFiltro = (cards, rango) => {
  if (!rango) return cards;
  return cards.filter((card) => {
    const fecha = toLocalDateStr(card.date);
    if (!fecha) return false;
    return fecha >= rango.inicio && fecha <= rango.fin;
  });
};

const hoy = new Date();
const defaultDia = toLocalDateStr(hoy);
const defaultSemana = (() => {
  const d = new Date(Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
})();
const defaultMes = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
const defaultAño = String(hoy.getFullYear());

// ─── KPI Card ─────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, color, alert }) => (
  <div className={`bg-white rounded-xl border shadow-sm p-4 flex flex-col gap-1 ${
    alert ? 'border-red-300 bg-red-50' : 'border-gray-100'
  }`}>
    <p className={`text-xs font-medium uppercase tracking-wide ${alert ? 'text-red-500' : 'text-gray-400'}`}>
      {label}
    </p>
    <p className={`text-2xl font-bold leading-tight ${color}`}>{value ?? '—'}</p>
    {sub && <p className="text-xs text-gray-400">{sub}</p>}
  </div>
);

// ─── Cards ────────────────────────────────────────────────────────────────────

const IncidentCard = ({ card, onCopy }) => {
  const dias = calcDias(card.date);
  const enAlerta = dias !== null && dias > UMBRAL_SEGUIMIENTO;
  return (
    <div className={`relative rounded-xl shadow-sm hover:shadow-md transition-all duration-150 group ${
      enAlerta ? 'bg-orange-50 border border-orange-200 hover:border-orange-300' : 'bg-white border border-gray-200 hover:border-blue-300'
    }`}>
      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className={`font-semibold text-sm leading-tight truncate flex-1 ${enAlerta ? 'text-orange-800' : 'text-gray-900'}`}>
            {card.reference}
          </span>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 flex-shrink-0">INC</span>
        </div>
        {card.customerName && <p className="text-xs text-gray-500 truncate mb-1">{card.customerName}</p>}
        <div className={`text-xs font-medium ${diasColor(dias)}`}>
          {dias === null ? 'Fecha desconocida' : dias === 0 ? 'Hoy' : `Hace ${dias} día${dias !== 1 ? 's' : ''}`}
        </div>
        {enAlerta && (
          <p className="text-[11px] text-orange-600 font-semibold mt-1">Pendiente de atención</p>
        )}
      </div>
      <button
        onClick={() => onCopy(card.reference)}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 rounded-md bg-white/90 hover:bg-white border border-gray-200 transition-all"
      >
        <Icons.Copy className="w-3 h-3 text-gray-500" />
      </button>
    </div>
  );
};

const WorkOrderCard = ({ card, onClick, onCopy }) => {
  const dias = calcDias(card.date);
  const enAlerta = dias !== null && dias > UMBRAL_SEGUIMIENTO;
  return (
    <div className={`relative rounded-xl shadow-sm hover:shadow-md transition-all duration-150 group ${
      enAlerta ? 'bg-orange-50 border border-orange-200 hover:border-orange-300' : 'bg-white border border-gray-200 hover:border-blue-300'
    }`}>
      <button onClick={() => onClick(card)} className="w-full text-left p-3.5 active:scale-[0.98]">
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className={`font-semibold text-sm leading-tight truncate flex-1 ${enAlerta ? 'text-orange-800' : 'text-gray-900 group-hover:text-blue-700'} transition-colors`}>
            {card.reference}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">ORD</span>
            {card.pdfPath && <Icons.FileText className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-400 transition-colors" />}
          </div>
        </div>
        {card.customerName && <p className="text-xs text-gray-500 truncate mb-1">{card.customerName}</p>}
        <div className={`text-xs font-medium ${diasColor(dias)}`}>
          {dias === null ? 'Fecha desconocida' : dias === 0 ? 'Hoy' : `Hace ${dias} día${dias !== 1 ? 's' : ''}`}
        </div>
        {enAlerta && (
          <p className="text-[11px] text-orange-600 font-semibold mt-1">Pendiente de atención</p>
        )}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onCopy(card.reference); }}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 rounded-md bg-white/90 hover:bg-white border border-gray-200 transition-all"
      >
        <Icons.Copy className="w-3 h-3 text-gray-500" />
      </button>
    </div>
  );
};

const PresupuestoCard = ({ card, onCopy, draggable: isDraggable, onDragStart, isDragging }) => {
  const dias = calcDias(card.date);
  const necesitaSeguimiento = card.documentStateId === ESTIMATE_STATES.enviado
    && dias !== null && dias > UMBRAL_SEGUIMIENTO;
  const enAlerta = necesitaSeguimiento || (dias !== null && dias > UMBRAL_SEGUIMIENTO * 3);

  const importe = card.totalAmount != null ? formatEUR(card.totalAmount) : null;

  return (
    <div
      draggable={isDraggable}
      onDragStart={isDraggable ? (e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart(card); } : undefined}
      className={`relative rounded-xl shadow-sm transition-all duration-150 group select-none ${
        isDragging ? 'opacity-40 scale-95' : 'hover:shadow-md'
      } ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''} ${
        necesitaSeguimiento
          ? 'bg-red-50 border border-red-300 hover:border-red-400'
          : enAlerta
          ? 'bg-amber-50 border border-amber-200 hover:border-amber-300'
          : 'bg-white border border-gray-200 hover:border-violet-300'
      }`}>
      <button
        onClick={() => card.pdfPath && window.open(card.pdfPath, '_blank', 'noopener,noreferrer')}
        className="w-full text-left p-3.5 active:scale-[0.98]"
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className={`font-semibold text-sm leading-tight truncate flex-1 ${
            necesitaSeguimiento ? 'text-red-800' : enAlerta ? 'text-amber-800' : 'text-gray-900 group-hover:text-violet-700'
          } transition-colors`}>
            {card.reference}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600">PRS</span>
            {card.pdfPath && <Icons.FileText className="w-3.5 h-3.5 text-gray-300 group-hover:text-violet-400 transition-colors" />}
          </div>
        </div>

        {importe && (
          <p className={`text-base font-bold mt-0.5 ${necesitaSeguimiento ? 'text-red-700' : 'text-gray-800'}`}>
            {importe}
          </p>
        )}

        {card.parentIncidentId && (
          <p className="text-[11px] text-blue-500 mt-0.5">Incidencia #{card.parentIncidentId}</p>
        )}

        <div className={`mt-1.5 text-xs font-medium ${diasColor(dias)}`}>
          {dias === null ? 'Fecha desconocida' : dias === 0 ? 'Hoy' : `Hace ${dias} día${dias !== 1 ? 's' : ''}`}
        </div>

        {necesitaSeguimiento && (
          <div className="mt-1.5 flex items-center gap-1 text-[11px] font-bold text-red-600">
            <span>⚠</span>
            <span>Llamar al cliente — {dias} días sin respuesta</span>
          </div>
        )}

        {card.signed && !necesitaSeguimiento && (
          <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600 mt-1">
            Firmado
          </span>
        )}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onCopy(card.reference); }}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 rounded-md bg-white/90 hover:bg-white border border-gray-200 transition-all"
      >
        <Icons.Copy className="w-3 h-3 text-gray-500" />
      </button>
    </div>
  );
};

// ─── Columna ─────────────────────────────────────────────────────────────────

const DROPPABLE_COLS = new Set(['presupuestoEnviado', 'presupuestoAceptado']);

const KanbanColumn = ({
  config, cards, onWorkOrderClick, onCopy,
  draggingCard, onDragStart, onDragOver, onDragLeave, onDrop, isDragOver,
}) => {
  const alertas = cards.filter((c) => {
    const d = calcDias(c.date);
    return d !== null && d > UMBRAL_SEGUIMIENTO;
  }).length;

  const valor    = config.showValue ? columnValue(cards) : null;
  const isTarget = DROPPABLE_COLS.has(config.key) && draggingCard && draggingCard.fromColumn !== config.key;

  return (
    <div
      className={`flex flex-col min-w-[270px] flex-1 rounded-2xl border-t-4 ${config.color} shadow-sm transition-all duration-150 ${
        isDragOver ? 'bg-violet-50 ring-2 ring-violet-400 ring-offset-1' : 'bg-gray-50'
      }`}
      onDragOver={isTarget ? (e) => { e.preventDefault(); onDragOver(config.key); } : undefined}
      onDragLeave={isTarget ? onDragLeave : undefined}
      onDrop={isTarget ? (e) => { e.preventDefault(); onDrop(config.key); } : undefined}
    >
      <div className={`${config.headerBg} rounded-t-xl px-4 py-3`}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${config.dotColor}`} />
            <h3 className={`font-bold text-sm ${config.headerText}`}>{config.label}</h3>
          </div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${config.badgeBg} ${config.badgeText}`}>
            {cards.length}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {valor != null && valor > 0 && (
            <span className={`text-xs font-bold ${config.headerText}`}>{formatEUR(valor)}</span>
          )}
          {alertas > 0 && (
            <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white">
              ⚠️ {alertas} {alertas === 1 ? 'alerta' : 'alertas'}
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 max-h-[calc(100vh-420px)]">
        {isDragOver && (
          <div className="border-2 border-dashed border-violet-400 rounded-xl p-4 text-center text-violet-500 text-xs font-semibold bg-violet-50">
            Soltar aquí
          </div>
        )}
        {cards.length === 0 && !isDragOver ? (
          <p className="text-center text-gray-400 text-xs py-8">{config.emptyMsg}</p>
        ) : (
          cards.map((card) => {
            const key = `${card.cardType}-${card.id}`;
            if (card.cardType === 'presupuesto') {
              const canDrag = DROPPABLE_COLS.has(config.key);
              return (
                <PresupuestoCard
                  key={key}
                  card={card}
                  onCopy={onCopy}
                  draggable={canDrag}
                  onDragStart={(c) => onDragStart(c, config.key)}
                  isDragging={draggingCard?.card.id === card.id}
                />
              );
            }
            if (card.cardType === 'incident') return <IncidentCard key={key} card={card} onCopy={onCopy} />;
            return <WorkOrderCard key={key} card={card} onClick={onWorkOrderClick} onCopy={onCopy} />;
          })
        )}
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const KanbanComercialView = () => {
  const [workOrdersPresupuesto, setWorkOrdersPresupuesto] = useState([]);
  const [loadingWO, setLoadingWO]   = useState(true);
  const [errorWO, setErrorWO]       = useState('');

  const [bolsa, setBolsa]           = useState([]);
  const [loadingBolsa, setLoadingBolsa] = useState(true);
  const [errorBolsa, setErrorBolsa] = useState('');

  const [presupuestos, setPresupuestos] = useState([]);
  const [loadingPRS, setLoadingPRS] = useState(true);
  const [errorPRS, setErrorPRS]     = useState('');

  const [pendingUpdates, setPendingUpdates] = useState({});
  const [dragging, setDragging]     = useState(null); // { card, fromColumn }
  const [dragOverCol, setDragOverCol] = useState(null);
  const [toast, setToast]           = useState('');
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('todo');
  const [valDia, setValDia]         = useState(defaultDia);
  const [valSemana, setValSemana]   = useState(defaultSemana);
  const [valMes, setValMes]         = useState(defaultMes);
  const [valAño, setValAño]         = useState(defaultAño);

  const cargarWorkOrders = useCallback(async (force = false) => {
    setLoadingWO(true); setErrorWO('');
    try {
      const res = await fetch('/.netlify/functions/getKanbanWorkOrders', { headers: { ...authService.authHeader() }, ...(force ? { cache: 'reload' } : {}) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setWorkOrdersPresupuesto(data.columnas?.pendientesPresupuesto || []);
    } catch (e) { setErrorWO(e.message); }
    finally { setLoadingWO(false); }
  }, []);

  const cargarBolsa = useCallback(async (force = false) => {
    setLoadingBolsa(true); setErrorBolsa('');
    try {
      const res = await fetch('/.netlify/functions/getBolsaTrabajo', { headers: { ...authService.authHeader() }, ...(force ? { cache: 'reload' } : {}) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setBolsa(data.bolsa || []);
    } catch (e) { setErrorBolsa(e.message); }
    finally { setLoadingBolsa(false); }
  }, []);

  const cargarPresupuestos = useCallback(async (force = false) => {
    setLoadingPRS(true); setErrorPRS('');
    try {
      const res = await fetch('/.netlify/functions/getWorkEstimates', { headers: { ...authService.authHeader() }, ...(force ? { cache: 'reload' } : {}) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setPresupuestos(data.presupuestos || []);
    } catch (e) { setErrorPRS(e.message); }
    finally { setLoadingPRS(false); }
  }, []);

  const cargarTodo = useCallback((force = false) => {
    setAlertDismissed(false);
    cargarWorkOrders(force);
    cargarBolsa(force);
    cargarPresupuestos(force);
  }, [cargarWorkOrders, cargarBolsa, cargarPresupuestos]);

  React.useEffect(() => { cargarTodo(); }, [cargarTodo]);

  const valorActual = { dia: valDia, semana: valSemana, mes: valMes, año: valAño }[filtroTipo] ?? null;
  const rango = useMemo(() => getRango(filtroTipo, valorActual), [filtroTipo, valorActual]);

  // Datos tipados
  const incidentesPresupuesto = useMemo(
    () => bolsa.filter((c) => c.incidentTypeId === PRESUPUESTO_TYPE_ID).map((c) => ({ ...c, cardType: 'incident' })),
    [bolsa]
  );
  const workOrdersTyped = useMemo(
    () => workOrdersPresupuesto.map((c) => ({ ...c, cardType: 'workorder' })),
    [workOrdersPresupuesto]
  );
  // Aplicar cambios optimistas de estado (drag-and-drop) antes de distribuir en columnas
  const presupuestosTyped = useMemo(
    () => presupuestos.map((c) => ({
      ...c,
      documentStateId: pendingUpdates[c.id] ?? c.documentStateId,
      cardType: 'presupuesto',
    })),
    [presupuestos, pendingUpdates]
  );

  // ─── Drag-and-drop handlers ───────────────────────────────────────────────

  const handleDragStart = useCallback((card, fromColumn) => {
    setDragging({ card, fromColumn });
  }, []);

  const handleDragOver = useCallback((colKey) => {
    setDragOverCol(colKey);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverCol(null);
  }, []);

  const handleDrop = useCallback(async (targetColKey) => {
    if (!dragging || targetColKey === dragging.fromColumn) {
      setDragging(null); setDragOverCol(null); return;
    }
    const { card } = dragging;
    const newStateId = targetColKey === 'presupuestoAceptado'
      ? ESTIMATE_STATES.aceptado
      : ESTIMATE_STATES.enviado;
    const label = targetColKey === 'presupuestoAceptado' ? 'Aceptado' : 'Enviado';

    // Actualización optimista
    setPendingUpdates((prev) => ({ ...prev, [card.id]: newStateId }));
    setDragging(null); setDragOverCol(null);

    try {
      const res = await fetch('/.netlify/functions/updateWorkEstimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ estimateId: card.id, documentStateId: newStateId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      setToast(`${card.reference} → ${label}`);
      setTimeout(() => setToast(''), 3000);
    } catch (err) {
      // Revertir si falla
      setPendingUpdates((prev) => { const n = { ...prev }; delete n[card.id]; return n; });
      setToast(`Error al actualizar: ${err.message}`);
      setTimeout(() => setToast(''), 4000);
    }
  }, [dragging]);

  // KPIs — calculados sobre los presupuestos del periodo seleccionado (mismo rango que las columnas)
  const kpis = useMemo(() => {
    const prsEnRango = aplicarFiltro(presupuestos, rango);

    const cerrados   = prsEnRango.filter((p) => p.documentStateId === ESTIMATE_STATES.cerrado);
    const rechazados = prsEnRango.filter((p) => p.documentStateId === ESTIMATE_STATES.rechazado);
    const enviados   = prsEnRango.filter((p) => p.documentStateId === ESTIMATE_STATES.enviado);
    const aceptados  = prsEnRango.filter((p) => p.documentStateId === ESTIMATE_STATES.aceptado);
    const borradores = prsEnRango.filter((p) => !KNOWN_STATES.has(p.documentStateId));

    const valorPipeline = [...enviados, ...borradores, ...aceptados].reduce((s, p) => s + (p.totalAmount || 0), 0);
    // Facturación ganada = cerrados (presupuesto convertido en orden de trabajo real)
    const valorGanado   = cerrados.reduce((s, p) => s + (p.totalAmount || 0), 0);

    const conImporte = prsEnRango.filter((p) => p.totalAmount > 0);
    const ticketMedio = conImporte.length > 0
      ? conImporte.reduce((s, p) => s + p.totalAmount, 0) / conImporte.length
      : 0;

    // Tasa de cierre = cerrados / (cerrados + rechazados)
    // Cerrado = se creó la orden de trabajo (éxito real)
    // Rechazado = el cliente no aceptó
    const decididos = cerrados.length + rechazados.length;
    const tasaConversion = decididos > 0 ? Math.round((cerrados.length / decididos) * 100) : null;

    const sinRespuesta = enviados.filter((p) => {
      const d = calcDias(p.date);
      return d !== null && d > UMBRAL_SEGUIMIENTO;
    });

    const leadsEnCola = incidentesPresupuesto.length + workOrdersTyped.length;

    return {
      valorPipeline,
      valorGanado,
      ticketMedio,
      tasaConversion,
      sinRespuesta,
      leadsEnCola,
      totalCerrados: cerrados.length,
    };
  }, [presupuestos, incidentesPresupuesto, workOrdersTyped, rango]);

  // Columnas con distribución por estado y ordenación comercial
  const columnasFiltradas = useMemo(() => {
    const prsCol1      = presupuestosTyped.filter((p) => !KNOWN_STATES.has(p.documentStateId));
    const prsEnviado   = presupuestosTyped.filter((p) => p.documentStateId === ESTIMATE_STATES.enviado);
    const prsAceptado  = presupuestosTyped.filter((p) => p.documentStateId === ESTIMATE_STATES.aceptado);

    // Col 1: leads (ordenados más antiguo primero = más urgentes) + borradores PRS
    const col1Raw = [...workOrdersTyped, ...incidentesPresupuesto, ...prsCol1];
    col1Raw.sort((a, b) => {
      const da = calcDias(a.date) ?? 0;
      const db = calcDias(b.date) ?? 0;
      return db - da; // más antiguos primero
    });

    // Col 2: enviados ordenados más antiguo primero (seguimiento urgente)
    const enviados = [...prsEnviado].sort((a, b) => {
      const da = calcDias(a.date) ?? 0;
      const db = calcDias(b.date) ?? 0;
      return db - da;
    });

    // Col 3: aceptados ordenados por importe descendente (mayores primero)
    const aceptados = [...prsAceptado].sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0));

    return {
      pendientesPresupuesto: aplicarFiltro(col1Raw,   rango),
      presupuestoEnviado:    aplicarFiltro(enviados,  rango),
      presupuestoAceptado:   aplicarFiltro(aceptados, rango),
    };
  }, [workOrdersTyped, incidentesPresupuesto, presupuestosTyped, rango]);

  const loading = loadingWO || loadingBolsa || loadingPRS;
  const total   = Object.values(columnasFiltradas).reduce((acc, col) => acc + col.length, 0);

  const handleWorkOrderClick = (card) => {
    if (card.pdfPath) window.open(card.pdfPath, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = useCallback((reference) => {
    navigator.clipboard.writeText(reference).then(() => {
      setToast(reference);
      setTimeout(() => setToast(''), 2000);
    });
  }, []);

  const años = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => String(current - i));
  }, []);

  const rangoLabel = rango ? `${rango.inicio} → ${rango.fin}` : null;
  const errorMsg   = [errorWO, errorBolsa, errorPRS].filter(Boolean).join(' | ');

  const tasaColor = kpis.tasaConversion === null ? 'text-gray-400'
    : kpis.tasaConversion >= 60 ? 'text-emerald-600'
    : kpis.tasaConversion >= 35 ? 'text-amber-600'
    : 'text-red-600';

  return (
    <div className="space-y-4">

      {/* ── Header ── */}
      <div className="bg-white rounded-2xl shadow-lg border p-5">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Icons.DollarSign className="w-6 h-6 text-blue-600" />
              Kanban Comercial
            </h2>
            <p className="text-gray-500 text-sm mt-0.5">Pipeline de presupuestos · métricas en tiempo real</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {!loading && (
              <span className="text-sm text-gray-500">
                <strong className="text-gray-800">{total}</strong> en pipeline
              </span>
            )}
            <button
              onClick={() => cargarTodo(true)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              <Icons.Refresh className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Cargando...' : 'Actualizar'}
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
            {FILTROS.map((f) => (
              <button key={f} onClick={() => setFiltroTipo(f)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filtroTipo === f ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}>
                {FILTROS_LABEL[f]}
              </button>
            ))}
          </div>
          {filtroTipo === 'dia'    && <input type="date"  value={valDia}    onChange={(e) => setValDia(e.target.value)}    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300" />}
          {filtroTipo === 'semana' && <input type="week"  value={valSemana} onChange={(e) => setValSemana(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300" />}
          {filtroTipo === 'mes'    && <input type="month" value={valMes}    onChange={(e) => setValMes(e.target.value)}    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300" />}
          {filtroTipo === 'año'    && (
            <select value={valAño} onChange={(e) => setValAño(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300">
              {años.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          )}
          {rangoLabel && <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">{rangoLabel}</span>}
        </div>
      </div>

      {/* ── KPIs ── */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard
            label="Pipeline activo"
            value={formatEUR(kpis.valorPipeline)}
            sub="Borradores + enviados"
            color="text-blue-700"
          />
          <KpiCard
            label="Facturación ganada"
            value={formatEUR(kpis.valorGanado)}
            sub={`${kpis.totalCerrados} presupuesto${kpis.totalCerrados !== 1 ? 's' : ''} cerrado${kpis.totalCerrados !== 1 ? 's' : ''}`}
            color="text-emerald-700"
          />
          <KpiCard
            label="Tasa de cierre"
            value={kpis.tasaConversion !== null ? `${kpis.tasaConversion}%` : null}
            sub="Aceptados / decididos"
            color={tasaColor}
          />
          <KpiCard
            label="Ticket medio"
            value={kpis.ticketMedio > 0 ? formatEUR(kpis.ticketMedio) : null}
            sub="Por presupuesto"
            color="text-violet-700"
          />
          <KpiCard
            label="Requieren seguimiento"
            value={kpis.sinRespuesta.length > 0 ? kpis.sinRespuesta.length : '✓'}
            sub={kpis.sinRespuesta.length > 0 ? `Enviados > ${UMBRAL_SEGUIMIENTO} días sin respuesta` : 'Todo al día'}
            color={kpis.sinRespuesta.length > 0 ? 'text-red-600' : 'text-emerald-600'}
            alert={kpis.sinRespuesta.length > 0}
          />
          <KpiCard
            label="Leads en cola"
            value={kpis.leadsEnCola > 0 ? kpis.leadsEnCola : '✓'}
            sub={kpis.leadsEnCola > 0 ? 'Sin presupuesto aún' : 'Sin leads pendientes'}
            color={kpis.leadsEnCola > 0 ? 'text-amber-600' : 'text-emerald-600'}
          />
        </div>
      )}

      {/* ── Alerta de seguimiento ── */}
      {!loading && !alertDismissed && kpis.sinRespuesta.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="text-red-500 text-xl flex-shrink-0 mt-0.5">⚠️</span>
            <div>
              <p className="text-sm font-bold text-red-700">
                {kpis.sinRespuesta.length === 1
                  ? '1 presupuesto lleva más de 7 días enviado sin respuesta'
                  : `${kpis.sinRespuesta.length} presupuestos llevan más de 7 días enviados sin respuesta`}
              </p>
              <p className="text-xs text-red-500 mt-0.5">
                Máximo impacto: llamar al cliente antes de que busque otra empresa.
                {kpis.sinRespuesta.length > 0 && ` Referencias: ${kpis.sinRespuesta.slice(0, 3).map(p => p.reference).join(', ')}${kpis.sinRespuesta.length > 3 ? '...' : ''}`}
              </p>
            </div>
          </div>
          <button onClick={() => setAlertDismissed(true)} className="text-red-400 hover:text-red-600 flex-shrink-0 p-1">
            <Icons.X className="w-4 h-4" />
          </button>
        </div>
      )}

      {errorMsg && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
          Error al cargar datos: {errorMsg}
        </div>
      )}

      {/* ── Leyenda ── */}
      {!loading && (
        <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500 px-1">
          <span className="flex items-center gap-1.5">
            <span className="font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600">INC</span>
            Incidencia (solicitud de presupuesto)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">ORD</span>
            Orden pendiente de presupuesto
          </span>
          <span className="flex items-center gap-1.5">
            <span className="font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600">PRS</span>
            Presupuesto creado
          </span>
          <span className="flex items-center gap-1.5 text-gray-400">
            Columna 1 ordenada por antigüedad · Columna 2 por urgencia de seguimiento · Columna 3 por importe
          </span>
        </div>
      )}

      {/* ── Skeleton ── */}
      {loading && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNAS_CONFIG.map((c) => (
            <div key={c.key} className={`min-w-[270px] flex-1 rounded-2xl border-t-4 ${c.color} bg-gray-50 shadow-sm`}>
              <div className={`${c.headerBg} rounded-t-xl px-4 py-3`}>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3" />
              </div>
              <div className="p-3 space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4 mb-2" />
                    <div className="h-2 bg-gray-100 rounded animate-pulse w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Kanban ── */}
      {!loading && (
        <div
          className="flex gap-4 overflow-x-auto pb-4"
          onDragEnd={() => { setDragging(null); setDragOverCol(null); }}
        >
          {COLUMNAS_CONFIG.map((config) => (
            <KanbanColumn
              key={config.key}
              config={config}
              cards={columnasFiltradas[config.key] || []}
              onWorkOrderClick={handleWorkOrderClick}
              onCopy={handleCopy}
              draggingCard={dragging}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              isDragOver={dragOverCol === config.key}
            />
          ))}
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-50 text-white text-sm font-medium px-4 py-2 rounded-xl shadow-xl flex items-center gap-2 ${
          toast.startsWith('Error') ? 'bg-red-600' : 'bg-gray-900'
        }`}>
          {toast.startsWith('Error')
            ? <Icons.X className="w-4 h-4 text-red-200" />
            : <Icons.Copy className="w-4 h-4 text-green-400" />
          }
          <span>{toast.startsWith('Error') ? toast : <>Movido: <strong>{toast}</strong></>}</span>
        </div>
      )}
    </div>
  );
};

export default KanbanComercialView;
